import JSZip from 'jszip'
import {
  ZIP_STRICT_TYPES,
  RECIPE_PREVIEW_PREFIX,
  RECIPE_COVER_PREFIX,
  RECIPE_CONTENT,
  PUZZLE_IMAGE_PREFIX,
  PUZZLE_PREVIEW_PREFIX,
  LEVEL_IMAGE1_PREFIX,
  LEVEL_IMAGE2_PREFIX,
  LEVEL_CONFIG,
  RECIPE_V2_PREVIEW_SUFFIX,
  RECIPE_V2_JSON_SUFFIX
} from './constants'
import { isZipOnlyAssetType } from '../form/genericResourceZipPackageSpec'
import { inspectZipStructure } from '../form/genericResourceZipStructureCheck'
import { validateLocalFileSizeForAssetType } from '../genericResourceUploadRules'

/**
 * 判断文件名是否匹配「主名前缀 + 单段后缀」协议（镜像 MusicCmdExe#matchesEntryNamePrefix）。
 * 仅用于旧严格类型的内容级补检（level / recipe_v2 JSON）。
 */
function matchesEntryNamePrefix(fileName, expectedPrefix) {
  if (!fileName || !expectedPrefix) return false
  const normalizedFileName = fileName.toLowerCase()
  const normalizedPrefix = expectedPrefix.toLowerCase()
  if (!normalizedFileName.startsWith(normalizedPrefix)) return false
  const extensionPart = normalizedFileName.substring(normalizedPrefix.length)
  return extensionPart.length > 0 && !extensionPart.includes('.')
}

/**
 * 是否忽略 zip 条目路径：macOS 元数据目录、路径中任一节为隐藏名（以 . 开头）。
 */
function isIgnoredZipEntryPath(relativePath) {
  if (!relativePath) return true
  const normalized = String(relativePath).replace(/\\/g, '/')
  const segments = normalized.split('/').filter(Boolean)
  return segments.some((seg) => seg === '__MACOSX' || seg.startsWith('.'))
}

/**
 * 从 zip 条目中提取有效文件名列表（去掉目录层级，只保留 basename）。
 */
function collectEntryNames(zip) {
  const names = []
  zip.forEach((relativePath, entry) => {
    if (entry.dir) return
    if (isIgnoredZipEntryPath(relativePath)) return
    const base = relativePath.split('/').filter(Boolean).pop()
    if (base) names.push({ base, entry })
  })
  return names
}

function validateRecipeEntries(entries) {
  let hasPreview = false
  let hasCover = false
  let hasContent = false
  entries.forEach(({ base }) => {
    if (matchesEntryNamePrefix(base, RECIPE_PREVIEW_PREFIX)) hasPreview = true
    if (matchesEntryNamePrefix(base, RECIPE_COVER_PREFIX)) hasCover = true
    if (base === RECIPE_CONTENT) hasContent = true
  })
  if (!hasPreview || !hasCover || !hasContent) {
    return {
      ok: false,
      message: '上传的zip包中缺少名为 preview.*、cover.* 或 content.md 的文件'
    }
  }
  return { ok: true }
}

function validatePuzzleEntries(entries) {
  let hasImage = false
  let hasPreview = false
  entries.forEach(({ base }) => {
    if (matchesEntryNamePrefix(base, PUZZLE_IMAGE_PREFIX)) hasImage = true
    if (matchesEntryNamePrefix(base, PUZZLE_PREVIEW_PREFIX)) hasPreview = true
  })
  if (!hasImage || !hasPreview) {
    return {
      ok: false,
      message: '上传的zip包中缺少名为 image.* 或 preview.* 的图片文件'
    }
  }
  return { ok: true }
}

async function validateLevelEntries(entries) {
  let hasImage1 = false
  let hasImage2 = false
  let configEntry = null
  entries.forEach(({ base, entry }) => {
    if (matchesEntryNamePrefix(base, LEVEL_IMAGE1_PREFIX)) hasImage1 = true
    if (matchesEntryNamePrefix(base, LEVEL_IMAGE2_PREFIX)) hasImage2 = true
    if (base === LEVEL_CONFIG) configEntry = entry
  })
  if (!hasImage1 || !hasImage2 || !configEntry) {
    return {
      ok: false,
      message: '上传的zip包中缺少名为 image1.*、image2.* 或 config.json 的文件'
    }
  }
  try {
    const text = await configEntry.async('string')
    JSON.parse(text)
  } catch (e) {
    return {
      ok: false,
      message: 'config.json 解析失败，请检查 JSON 格式'
    }
  }
  return { ok: true }
}

async function validateRecipeV2Entries(entries) {
  const valid = entries
  if (valid.length !== 2) {
    return {
      ok: false,
      message: '上传的zip包异常：必须且只能包含2个有效文件(一张_preview.jpg和一个.json)'
    }
  }
  let previewEntry = null
  let jsonEntry = null
  for (const { base, entry } of valid) {
    const lower = base.toLowerCase()
    if (lower.endsWith(RECIPE_V2_PREVIEW_SUFFIX)) {
      previewEntry = entry
    } else if (lower.endsWith(RECIPE_V2_JSON_SUFFIX)) {
      jsonEntry = entry
    } else {
      return {
        ok: false,
        message: `包内包含非法后缀文件: ${base}`
      }
    }
  }
  if (!previewEntry || !jsonEntry) {
    return {
      ok: false,
      message: '包内缺少预览图(_preview.jpg)或配置文件(.json)'
    }
  }
  try {
    const text = await jsonEntry.async('string')
    JSON.parse(text)
  } catch (e) {
    return {
      ok: false,
      message: '.json 解析失败，请检查 JSON 格式'
    }
  }
  return { ok: true }
}

/**
 * 旧严格类型在「文件名契约」之外的内容级补检（JSON 可解析 / recipe_v2 恰好 2 文件）。
 * workout / badge / article 等只依赖 inspectZipStructure。
 */
async function validateLegacyStrictContent(rawFile, resourceType) {
  if (!ZIP_STRICT_TYPES.includes(resourceType)) {
    return { ok: true }
  }

  let zip
  try {
    zip = await JSZip.loadAsync(rawFile)
  } catch (e) {
    return { ok: false, message: '无法读取 zip 文件，请检查文件是否损坏' }
  }

  const entries = collectEntryNames(zip)
  if (!entries.length) {
    return { ok: false, message: 'zip 包为空' }
  }

  switch (resourceType) {
    case 'recipe(食谱)':
      return validateRecipeEntries(entries)
    case 'puzzle(拼图)':
      return validatePuzzleEntries(entries)
    case 'level(关卡)':
      return validateLevelEntries(entries)
    case 'recipe_v2':
      return validateRecipeV2Entries(entries)
    default:
      return { ok: true }
  }
}

/**
 * 对单个文件做前端校验：
 * 1) 贴纸 zip 大小上限
 * 2) zip-only 类型强制 .zip + Registry 必传文件名（inspectZipStructure）
 * 3) 旧严格类型再做 JSON / 文件数等深层补检
 *
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
export async function validateResourceFile(rawFile, resourceType) {
  if (!rawFile) {
    return { ok: false, message: '文件无效' }
  }

  // 先看大小：超限的包不必再解压检查结构
  const sizeCheck = validateLocalFileSizeForAssetType(resourceType, rawFile.size)
  if (!sizeCheck.ok) {
    return sizeCheck
  }

  const lowerName = (rawFile.name || '').toLowerCase()
  const zipOnly = isZipOnlyAssetType(resourceType)

  if (zipOnly && !lowerName.endsWith('.zip')) {
    return {
      ok: false,
      message: `${resourceType} 类型仅支持 .zip 格式`
    }
  }

  if (zipOnly) {
    let structure
    try {
      structure = await inspectZipStructure(rawFile, resourceType)
    } catch (e) {
      return { ok: false, message: '无法读取 zip 文件，请检查文件是否损坏' }
    }
    if (!structure.ok) {
      const missing = (structure.missing || []).join('、')
      return {
        ok: false,
        message: missing
          ? `ZIP 包结构不完整：缺少 ${missing}`
          : 'ZIP 包结构不完整'
      }
    }
  }

  return validateLegacyStrictContent(rawFile, resourceType)
}

/** 批量弹窗：是否按 zip 包契约提示 / 拦截 */
export function isZipPackageUploadType(resourceType) {
  return isZipOnlyAssetType(resourceType)
}

/** @deprecated 用 isZipPackageUploadType；保留别名避免旧引用断裂 */
export function isZipStrictType(resourceType) {
  return isZipPackageUploadType(resourceType)
}
